import classNames from 'classnames'
import React, { InputHTMLAttributes, ReactNode } from 'react'

export interface ToggleProps {
    /**
     * ID attribute.
     */
    id: string
    /**
     * Toggle label.
     */
    label: ReactNode
    /**
     * Additional custom class.
     */
    className?: string
    /**
     * If toggle is checked.
     * Use to handle state outside of component, should use with onChange prop.
     */
    checked?: boolean
    /**
     * Properties to be passed on the input element.
     */
    input?: InputHTMLAttributes<HTMLInputElement>
    /**
     * If check is disabled
     */
    isDisabled?: boolean
    /**
     * If check is invalid
     */
    isInvalid?: boolean
    /**
     * onChange handler.
     */
    onChange?: (checked: boolean) => void
    /**
     * Alternative toggle label to display when toggle is switched “on”.
     */
    onLabel?: ReactNode
}

export const Toggle = (props: ToggleProps): JSX.Element => {
    const { checked, onChange, isDisabled, isInvalid } = props

    const handleChange = (): void => {
        const nextChecked = !checked

        if (isDisabled) {
            return
        }

        if (onChange) {
            onChange(nextChecked)
        }
    }

    const className: string = classNames('toggle', isInvalid && 'is-invalid', props.className)

    const inputClassName: string = classNames('toggle__input', props.input?.className)

    const offText = <span className="toggle__text toggle__text--off">{props.label}</span>

    const onText = <span className="toggle__text toggle__text--on">{props.onLabel}</span>

    return (
        <div className={className}>
            <input
                {...props.input}
                type="checkbox"
                id={props.id}
                className={inputClassName}
                onChange={handleChange}
                checked={checked}
                defaultChecked={undefined}
                disabled={isDisabled}
            />
            <label className="toggle__label" htmlFor={props.id}>
                <span className="toggle__indicator" />
                {props.onLabel && checked ? onText : offText}
            </label>
        </div>
    )
}
